import { useApiStatusQuery } from "../hooks/use-api-status-query.ts";
import type {
  ApiQuota,
  ApiStatusProvider,
  ApiStatusRunSummary,
  TgeProximityScanSummary,
} from "../types/index.ts";

function formatProviderName(slug: string): string {
  const names: Record<string, string> = {
    twitterscore: "TwitterScore",
    rootdata: "RootData",
    defillama: "DefiLlama",
    "exa-tge": "Exa (TGE Proximity)",
  };
  return names[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1);
}

function RunSummaryRow({ label, data }: { label: string; data: ApiStatusRunSummary }) {
  const rate = data.total > 0 ? ((data.completed / data.total) * 100).toFixed(0) : null;
  return (
    <div className="api-run-row">
      <span className="api-run-row__label">{label}</span>
      <div className="api-run-row__cell">
        <span className="api-run-row__value">{data.total}</span>
      </div>
      <div className="api-run-row__cell">
        <span className="api-run-row__value api-run-row__value--positive">{data.completed}</span>
      </div>
      <div className="api-run-row__cell">
        <span className="api-run-row__value api-run-row__value--negative">{data.failed}</span>
      </div>
      <div className="api-run-row__cell">
        <span
          className={`api-run-row__value ${rate && Number(rate) >= 80 ? "api-run-row__value--positive" : "api-run-row__value--negative"}`}
        >
          {rate ? `${rate}%` : "—"}
        </span>
      </div>
      <div className="api-run-row__cell">
        <span className="api-run-row__value">
          {data.avgDurationMs ? `${(data.avgDurationMs / 1000).toFixed(1)}s` : "—"}
        </span>
      </div>
    </div>
  );
}

function QuotaCard({ q, tgeScan }: { q: ApiQuota; tgeScan?: TgeProximityScanSummary }) {
  const unitLabel = q.unit === "points" ? "pts" : q.unit === "credits" ? "credits" : "reqs";
  const isUsageBased = q.monthLimit === 0;
  const pctUsed = q.usagePercent;
  const pctClass = isUsageBased
    ? "healthy"
    : pctUsed >= 90
      ? "critical"
      : pctUsed >= 70
        ? "warning"
        : "healthy";

  const scanInfo = tgeScan && tgeScan.status !== "never_run" ? tgeScan : null;
  const scanAgo = scanInfo?.lastRunAt
    ? `${Math.floor((Date.now() - new Date(scanInfo.lastRunAt).getTime()) / 3600000)}h ago`
    : null;

  return (
    <div className="api-quota-card">
      <div className="api-quota-card__header">
        <span className="api-quota-card__name">{formatProviderName(q.provider)}</span>
        {isUsageBased ? (
          <span className="api-quota-card__badge api-quota-card__badge--healthy">
            {scanAgo ?? "Usage-based"}
          </span>
        ) : (
          <span className={`api-quota-card__badge api-quota-card__badge--${pctClass}`}>
            {(100 - pctUsed).toFixed(0)}% left
          </span>
        )}
      </div>

      {!isUsageBased && (
        <div className="api-quota-card__bar">
          <div
            className={`api-quota-card__fill api-quota-card__fill--${pctClass}`}
            style={{ width: `${Math.min(pctUsed, 100)}%` }}
          />
        </div>
      )}

      <div className="api-quota-card__details">
        {scanInfo ? (
          <>
            <div className="api-quota-card__detail">
              <span className="api-quota-card__detail-value">{scanInfo.scannedCount ?? "—"}</span>
              <span className="api-quota-card__detail-label">scanned</span>
            </div>
            <div className="api-quota-card__detail">
              <span className="api-quota-card__detail-value">
                {scanInfo.signalsDetected ?? "—"}
              </span>
              <span className="api-quota-card__detail-label">signals</span>
            </div>
            <div className="api-quota-card__detail">
              <span
                className={`api-quota-card__detail-value${(scanInfo.errors ?? 0) > 0 ? " api-quota-card__detail-value--negative" : ""}`}
              >
                {scanInfo.errors ?? 0}
              </span>
              <span className="api-quota-card__detail-label">errors</span>
            </div>
          </>
        ) : (
          <div className="api-quota-card__detail">
            <span className="api-quota-card__detail-value">{q.monthUsage.toLocaleString()}</span>
            <span className="api-quota-card__detail-label">used {unitLabel}</span>
          </div>
        )}
        {!isUsageBased && (
          <>
            <div className="api-quota-card__detail">
              <span className="api-quota-card__detail-value">{q.monthLimit.toLocaleString()}</span>
              <span className="api-quota-card__detail-label">limit</span>
            </div>
            <div className="api-quota-card__detail">
              <span className="api-quota-card__detail-value">
                {Math.floor(q.monthRemaining / q.callsPerAudit).toLocaleString()}
              </span>
              <span className="api-quota-card__detail-label">audits left</span>
            </div>
          </>
        )}
        {q.estimatedMonthCostUsd != null && q.estimatedMonthCostUsd > 0 && (
          <div className="api-quota-card__detail">
            <span className="api-quota-card__detail-value">
              ${q.estimatedMonthCostUsd.toFixed(2)}
            </span>
            <span className="api-quota-card__detail-label">
              {isUsageBased ? "est. monthly" : "cost"}
            </span>
          </div>
        )}
      </div>

      {q.resetsAt && (
        <div className="api-quota-card__footer">
          {q.unit === "points" ? "Expires" : "Resets"} {new Date(q.resetsAt).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

function ProviderCard({ p }: { p: ApiStatusProvider }) {
  const rate24h =
    p.stats24h.calls > 0 ? ((p.stats24h.successes / p.stats24h.calls) * 100).toFixed(0) : null;

  return (
    <div className={`api-prov-card ${p.isCritical ? "api-prov-card--critical" : ""}`}>
      <div className="api-prov-card__header">
        <div className="api-prov-card__id">
          <span className={`api-prov-card__dot ${p.configured ? "configured" : "missing"}`} />
          <span className="api-prov-card__name">{p.name}</span>
        </div>
        <span className="api-prov-card__domain">{p.apiDomain}</span>
      </div>
      <div className="api-prov-card__metrics">
        <div className="api-prov-card__metric">
          <span className="api-prov-card__metric-val">{p.stats24h.calls}</span>
          <span className="api-prov-card__metric-key">24h calls</span>
        </div>
        <div className="api-prov-card__metric">
          <span
            className={`api-prov-card__metric-val ${
              rate24h && Number(rate24h) >= 80 ? "text-positive" : rate24h ? "text-warning" : ""
            }`}
          >
            {rate24h ? `${rate24h}%` : "—"}
          </span>
          <span className="api-prov-card__metric-key">success</span>
        </div>
        <div className="api-prov-card__metric">
          <span className="api-prov-card__metric-val">
            {p.stats24h.avgLatencyMs ? `${p.stats24h.avgLatencyMs}ms` : "—"}
          </span>
          <span className="api-prov-card__metric-key">latency</span>
        </div>
        <div className="api-prov-card__metric">
          <span className="api-prov-card__metric-val">
            {p.estimatedCostPerAudit > 0 ? `$${p.estimatedCostPerAudit}` : "Free"}
          </span>
          <span className="api-prov-card__metric-key">per audit</span>
        </div>
      </div>
      {p.stats24h.lastError && (
        <div className="api-prov-card__error">{p.stats24h.lastError.slice(0, 120)}</div>
      )}
    </div>
  );
}

export function ApiStatusView() {
  const apiStatusQuery = useApiStatusQuery(true);
  const apiStatusData = apiStatusQuery.data ?? null;
  const apiStatusLoading = apiStatusQuery.isLoading;

  return (
    <div className="api-status-view">
      {apiStatusLoading ? (
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Loading API status</span>
        </div>
      ) : apiStatusData ? (
        <>
          {(() => {
            const qs = (apiStatusData as unknown as Record<string, unknown>).queueStatus as
              | {
                  paused: boolean;
                  globalTrip: {
                    tripped: boolean;
                    trippedBy: string | null;
                    lastError: string | null;
                  };
                }
              | undefined;
            if (!qs?.paused) return null;
            return (
              <div className="queue-paused-banner">
                <div className="queue-paused-banner__title">SCORING PAUSED</div>
                <div className="queue-paused-banner__reason">
                  {qs.globalTrip.tripped
                    ? `Provider "${qs.globalTrip.trippedBy}" failed repeatedly`
                    : "Queues manually paused"}
                </div>
                {qs.globalTrip.lastError && (
                  <div className="queue-paused-banner__error">{qs.globalTrip.lastError}</div>
                )}
              </div>
            );
          })()}
          {/* Audit Runs */}
          <section className="api-status-view__section">
            <div className="api-status-view__section-header">
              <span className="section-title">Audit Runs</span>
              <button
                type="button"
                className="btn btn-small btn-secondary"
                onClick={() => void apiStatusQuery.refetch()}
              >
                Refresh
              </button>
            </div>
            <div className="api-run-panel">
              <div className="api-run-panel__head">
                <span className="api-run-panel__head-cell" />
                <span className="api-run-panel__head-cell">Runs</span>
                <span className="api-run-panel__head-cell">Passed</span>
                <span className="api-run-panel__head-cell">Failed</span>
                <span className="api-run-panel__head-cell">Success</span>
                <span className="api-run-panel__head-cell">Avg Time</span>
              </div>
              <RunSummaryRow label="24h" data={apiStatusData.runs24h} />
              <RunSummaryRow label="7d" data={apiStatusData.runs7d} />
              <RunSummaryRow label="30d" data={apiStatusData.runs30d} />
            </div>
          </section>

          {/* API Quotas */}
          {apiStatusData.quotas?.length > 0 && (
            <section className="api-status-view__section">
              <div className="api-status-view__section-header">
                <span className="section-title">API Quotas</span>
                <span className="section-count">{apiStatusData.quotas.length} tracked</span>
              </div>
              <div className="api-quota-grid">
                {apiStatusData.quotas.map((q) => (
                  <QuotaCard
                    key={q.provider}
                    q={q}
                    tgeScan={q.provider === "exa-tge" ? apiStatusData.tgeProximityScan : undefined}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Provider Health */}
          <section className="api-status-view__section">
            <div className="api-status-view__section-header">
              <span className="section-title">Provider Health</span>
              <span className="section-count">{apiStatusData.providers.length} providers</span>
            </div>
            <div className="api-prov-grid">
              {apiStatusData.providers.map((p) => (
                <ProviderCard key={p.slug} p={p} />
              ))}
            </div>
          </section>

          {/* API Keys */}
          <section className="api-status-view__section">
            <div className="api-status-view__section-header">
              <span className="section-title">API Keys</span>
              <span className="section-count">
                {apiStatusData.apiKeys.filter((k) => k.configured).length}/
                {apiStatusData.apiKeys.length} configured
              </span>
            </div>
            <div className="api-keys-panel">
              {apiStatusData.apiKeys.map((key) => (
                <div key={key.envVar} className="api-key-row">
                  <span
                    className={`api-prov-card__dot ${key.configured ? "configured" : "missing"}`}
                  />
                  <span
                    className={`api-key-row__name ${key.configured ? "" : "api-key-row__name--missing"}`}
                  >
                    {key.envVar}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : (
        <div className="empty-state">
          <h3>No status data</h3>
          <p>Click Refresh to load API status data.</p>
        </div>
      )}
    </div>
  );
}
